import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { prefetchAdmin, prefetchOwner, prefetchBooking } from "@/lib/prefetch";
import { Menu, X, User, ChevronDown } from "lucide-react";

export default function Header() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const isHome = loc.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const solid = scrolled || !isHome || open;
  const links = [
    { to: "/logements", label: t("nav.properties") },
    { to: "/conciergerie", label: t("nav.concierge") },
    { to: "/location-entreprises", label: "Entreprises" },
    { to: "/confier-mon-logement", label: t("nav.entrust") },
    { to: "/decouvrir-rouen", label: t("nav.blog") },
    { to: "/consigne", label: "Consigne bagages" },
    { to: "/contact", label: t("nav.contact") },
  ];

  const dashLink = user && user.role !== "traveler"
    ? (["super_admin", "admin"].includes(user.role) ? "/admin" : "/proprietaire")
    : null;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-nj-bg/95 backdrop-blur-md border-b border-nj-olive" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="logo-link" className="flex flex-col leading-none shrink-0 mr-4">
            <span className={`font-serif text-2xl md:text-3xl font-medium tracking-tight whitespace-nowrap ${solid ? "text-nj-primary" : "text-white"}`}>
              NJ Conciergerie
            </span>
            <span className={`text-[10px] tracking-[0.28em] uppercase mt-0.5 ${solid ? "text-nj-muted" : "text-white/80"}`}>
              Rouen
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to}`}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  solid ? "text-nj-text2 hover:text-nj-primary" : "text-white/90 hover:text-white"
                }`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4 shrink-0 ml-4">
            <LanguageSwitcher dark={!solid} />
            {user && user !== false ? (
              <div className="flex items-center gap-3">
                {dashLink && (
                  <Link to={dashLink} data-testid="nav-dashboard"
                    className={`text-sm font-medium ${solid ? "text-nj-primary" : "text-white"}`}>
                    {user.role === "owner" ? t("nav.owner") : t("nav.admin")}
                  </Link>
                )}
                <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}
                  className={`text-sm font-medium ${solid ? "text-nj-muted hover:text-nj-primary" : "text-white/80 hover:text-white"}`}>
                  {t("nav.logout")}
                </button>
              </div>
            ) : (
              <Link to="/connexion" data-testid="nav-login"
                onMouseEnter={() => { prefetchAdmin(); prefetchOwner(); }}
                className={`flex items-center gap-1.5 text-sm font-medium ${solid ? "text-nj-primary" : "text-white"}`}>
                <User className="w-4 h-4" /> {t("nav.login")}
              </Link>
            )}
            <Link to="/logements" data-testid="header-book-btn"
              onMouseEnter={prefetchBooking}
              className="bg-nj-primary text-white text-sm font-semibold px-5 py-2.5 rounded-sm hover:bg-nj-primaryHover transition-colors">
              {t("nav.book")}
            </Link>
          </div>

          <button data-testid="mobile-menu-btn" className="lg:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X className={solid ? "text-nj-primary" : "text-white"} />
                  : <Menu className={solid ? "text-nj-primary" : "text-white"} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-nj-bg border-t border-nj-olive px-4 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block text-nj-text font-medium py-1">{l.label}</Link>
          ))}
          <div className="pt-3 border-t border-nj-olive flex items-center justify-between">
            <LanguageSwitcher />
            {user && user !== false ? (
              <div className="flex gap-4">
                {dashLink && <Link to={dashLink} className="text-nj-primary font-medium">{user.role === "owner" ? t("nav.owner") : t("nav.admin")}</Link>}
                <button onClick={async () => { await logout(); nav("/"); }} className="text-nj-muted font-medium">{t("nav.logout")}</button>
              </div>
            ) : (
              <Link to="/connexion" className="text-nj-primary font-medium">{t("nav.login")}</Link>
            )}
          </div>
          <Link to="/logements" className="block text-center bg-nj-primary text-white font-semibold py-3 rounded-sm">{t("nav.book")}</Link>
        </div>
      )}
    </header>
  );
}
